import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  forwardRef,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  PaysafeEvent,
  PaysafeEventType,
  PaysafeField,
  PaysafeInstance,
} from '@checkout/util-models';
import {
  BaseFormControlComponent,
  FormControlDirective,
  FormValidityIconComponent,
  markControlAsTouched,
} from '@domgen/dgx-fe-forms/shared';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'checkout-paysafe-form-field',
  templateUrl: './paysafe-form-field.component.html',
  styleUrls: ['./paysafe-form-field.component.scss'],
  providers: [
    {
      provide: BaseFormControlComponent,
      useExisting: forwardRef(() => PaysafeFormFieldComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    FormControlDirective,
    FormValidityIconComponent,
    NgClass,
  ],
})
export class PaysafeFormFieldComponent extends BaseFormControlComponent<string> {
  constructor(
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly ngControl: NgControl,
    protected readonly destroyRef: DestroyRef
  ) {
    super(changeDetectorRef, ngControl, destroyRef);
  }

  @Input() fieldId?: string;
  @Input() set field(field: PaysafeField | undefined) {
    if (field) {
      this.bindFieldStatus(field);
    }
  }

  focused$ = new BehaviorSubject<boolean>(false);

  private bindFieldStatus(field: PaysafeField): void {
    const callback = (instance: PaysafeInstance, event: PaysafeEvent) =>
      this.onPaysafeEvent(event);

    field.blur(callback);
    field.focus(callback);
    field.invalid(callback);
    field.valid(callback);
  }

  private onPaysafeEvent(event: PaysafeEvent): void {
    switch (event.type) {
      case PaysafeEventType.Blur:
        this.focused$.next(false);
        markControlAsTouched(this.control);
        break;
      case PaysafeEventType.Focus:
        this.focused$.next(true);
        break;
      case PaysafeEventType.Valid:
        this.control.setValue(true);
        this.control.setErrors(null, { emitEvent: true });
        break;
      case PaysafeEventType.Invalid:
        this.control.setValue(undefined);
        this.control.setErrors(
          {
            ...(event.data?.isEmpty && { required: 'Please provide a value' }),
            ...(!event.data?.isEmpty && {
              pattern: 'Please provide a valid value',
            }),
          },
          { emitEvent: true }
        );
        break;
      default:
        break;
    }
  }
}
